/**
 * Browser side of Flux Agent photos: shrinking a camera frame or picked image to at most
 * 1600 pixels and re-encoding it through a canvas, which drops EXIF (including
 * GPS location) before anything leaves the device, and loading stored photos and clips through
 * credentialed `blob:` URLs.
 *
 * The canvas, `createImageBitmap` and `fetch` calls are thin bindings to browser APIs. The size
 * and type rules live in the shared native-tested Rust browser policy; the server repeats every check
 * (`flux_agent::media::validate_photo`) and strips metadata again, so nothing here is trusted.
 *
 * @module apps/web/src/lib/fluxAgentPhoto
 */
import { API_BASE } from './api';
import { photoPolicy } from './fluxAgentBrowser';

/** JPEG canvas encoding quality; source/type/size decisions live in Rust. */
export const PHOTO_QUALITY = 0.85;

/** True when this browser can open a live camera preview (`getUserMedia` in a secure context). */
export function canUseCamera(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    (typeof window === 'undefined' || window.isSecureContext !== false)
  );
}

/**
 * Binding: draw a decoded image or video frame onto a canvas at the Rust policy size and encode
 * it as JPEG. Re-encoding through a canvas writes only pixels, so EXIF, XMP and GPS never survive.
 */
export async function encodeFrame(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number
): Promise<Blob> {
  const { width, height } = await photoPolicy({ width: sourceWidth, height: sourceHeight });
  if (!width || !height) throw new Error('The photo has no size.');
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser cannot prepare photos.');
  context.imageSmoothingQuality = 'high';
  context.drawImage(source, 0, 0, width, height);
  const blob = await new Promise<Blob | null>(resolve =>
    canvas.toBlob(resolve, 'image/jpeg', PHOTO_QUALITY)
  );
  if (!blob) throw new Error('The photo could not be encoded.');
  return blob;
}

/**
 * Binding: decode a picked file (applying its EXIF orientation, so the result is upright) and
 * re-encode it with {@link encodeFrame}.
 */
export async function preparePickedPhoto(file: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    return await encodeFrame(bitmap, bitmap.width, bitmap.height);
  } finally {
    bitmap.close();
  }
}

/**
 * Binding: fetch owner-only media using the current session without a cross-user object-URL
 * cache. The mounting view owns and revokes the returned URL; abort stops an obsolete load.
 */
export async function loadMediaObjectUrl(
  path: string,
  fetcher: typeof fetch = fetch,
  signal?: AbortSignal
): Promise<string | null> {
  const relative = path.replace(/^\/api(?=\/)/, '');
  let token: string | null = null;
  try {
    token = localStorage.getItem('flexiplanner-session-token');
  } catch {
    /* Cookie authentication still applies. */
  }
  try {
    const response = await fetcher(`${API_BASE}${relative}`, {
      credentials: 'include',
      cache: 'no-store',
      signal,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const type = response.headers.get('Content-Type') || '';
    if (!response.ok || !/^(image|audio)\//.test(type)) return null;
    const blob = await response.blob();
    if (signal?.aborted) return null;
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

/** Format a clip length in milliseconds as `m:ss`. */
export function formatDuration(ms: number | null | undefined): string {
  if (!ms || ms < 0) return '';
  const seconds = Math.round(ms / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** File name for downloading a clip: the label made safe, with the extension for its type. */
export function downloadName(label: string | null | undefined, mimeType: string): string {
  const base =
    (label || 'flux-agent-sound')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'flux-agent-sound';
  return `${base}.${mimeType === 'audio/wav' ? 'wav' : 'mp3'}`;
}
